import { Content } from '@tiptap/react'
import { TCollabComment } from '@tiptap-pro/provider'
import { formatDistanceToNow } from 'date-fns'
import { useState } from 'react'
import { User } from '@opallabs/concepts'
import { Spacing } from '@opallabs/gem'
import { Avatar, Button, DropdownMenu, Icon } from '@opallabs/lapidary'
import { ThreadCommentData } from '../../CollaborationTextEditor/hooks/useThreads'
import { TextEditor } from '../../TextEditor'
import { TextEditorContent } from '../../TextEditor/content'
import { mergeExtensions } from '../../TextEditor/hooks'
import { ContentChain } from '../../TextEditor/utils'
import { CommentEditor } from '../CommentEditor'
import { useThread } from '../useThread'
import * as styles from './styles'

export interface CommentProps extends TCollabComment<ThreadCommentData> {
  user?: User
  content: Content
}
export const Comment: React.FC<CommentProps> = ({
  id,
  data: { userName, userAvatar, author_id },
  createdAt,
  content: commentContent,
  user,
}) => {
  const { onDeleteComment, onEditComment } = useThread()
  const [commentMode, setCommentMode] = useState<'viewing' | 'editing'>(
    'viewing'
  )

  const handleEdit = () => {
    setCommentMode('editing')
  }
  const handleSubmitEdit = (content: ContentChain) => {
    if (onEditComment && content) {
      onEditComment(id, content.json)
      setCommentMode('viewing')
    }
  }
  const handleDelete = () => {
    if (onDeleteComment) {
      onDeleteComment(id)
    }
  }

  return (
    <div css={styles.container}>
      <div css={styles.header}>
        <Avatar fullName={userName || ''} src={userAvatar} size={Spacing.L} />
        <span css={styles.name}>{userName || ''}</span>
        <span css={styles.timeDistance}>
          {formatDistanceToNow(new Date(createdAt), { addSuffix: true })}
        </span>
        <span style={{ flex: 1 }}></span>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <Button
              name="comment options"
              variant="ghost"
              icon={<Icon glyph="gem-more-horizontal" />}
              css={styles.optionButton}
            />
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content align="start" css={styles.optionMenu}>
              <DropdownMenu.Item
                disabled={author_id !== user?.uuid}
                css={styles.optionMenuItem}
                onSelect={handleEdit}
              >
                <Icon glyph="gem-edit" ariaHidden />
                Edit Comment
              </DropdownMenu.Item>
              <DropdownMenu.Item
                css={styles.optionMenuItem}
                destructive={true}
                disabled={author_id !== user?.uuid}
                onSelect={handleDelete}
              >
                <Icon glyph="gem-trash" ariaHidden />
                Delete Comment
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
      {commentMode === 'viewing' && commentContent && (
        <div css={styles.contentContainer}>
          <TextEditor
            initialValue={commentContent}
            contentStyle={[styles.content]}
            readOnly
            extensions={mergeExtensions({ mention: { config: true } })}
          >
            <TextEditorContent />
          </TextEditor>
        </div>
      )}
      {commentMode === 'editing' && (
        <div css={styles.textAndActionsContainer}>
          <CommentEditor
            initialValue={commentContent}
            onSubmit={handleSubmitEdit}
            onEscape={() => setCommentMode('viewing')}
          />
        </div>
      )}
    </div>
  )
}
